import { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import swal from "sweetalert";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";

import Modal from "../../../myfin_v2/elements/Modal";
import buttoncss from "../../../myfin_v2/css/button.module.css";
import InputGroup from "../../../myfin_v2/elements/InputGroup";
import { Button } from "../../../myfin_v2/elements/Button";
import commoncss from "../../../myfin_v2/css/common.module.css";

import { ReactComponent as CloseIcon } from "../../../myfin_v2/assets/icons/close.svg";
import { ReactComponent as CalenderIcon } from "../../../myfin_v2/assets/icons/calender.svg";
import { ReactComponent as DeleteGreyIcon } from "../../../myfin_v2/assets/icons/deleteGrey.svg";
import { ReactComponent as PlusPrimaryIcon } from "../../../myfin_v2/assets/icons/plusprimary.svg";

import ConsolDropdown from "../../elements/ConsolDropdown";
import {
  addConsolManualAdjustmentApiHandler,
  editConsolManualAdjustmentApiHandler,
  getConsolPopulateFSDetailsApiHandler,
} from "../../../WorkSpace/Components/ApiServices/apiHandler";

const CreateEditEntryModal = ({
  consolID,
  handleClose,
  editData,
  getManualAdjustments,
}) => {
  /* -------------------- BASIC STATE -------------------- */
  const [date, setDate] = useState(
    editData?.adjustmentDate ? dayjs(editData.adjustmentDate) : null
  );
  const [remarks, setRemarks] = useState(editData?.remarks || "");

  const [optionsData, setOptionsData] = useState([]);

  const [particulars, setParticulars] = useState([
    {
      levelone: null,
      levelTwo: null,
      levelThree: null,
      currentAmount: "",
      previousAmount: "",
    },
  ]);

  /* -------------------- LOAD POPULATE FS TREE -------------------- */
  useEffect(() => {
    getPopulateFS("PL");
  }, []);

  const getPopulateFS = (cat) => {
    getConsolPopulateFSDetailsApiHandler({
      category: cat,
      consolID,
    })
      .then((res) => {
        if (res?.data?.success) {
          setOptionsData(res.data.data || []);
        } else {
          swal("Error", "Unable to load FS structure", "error");
        }
      })
      .catch(() => {
        swal("Error", "Unable to load FS structure", "error");
      });
  };

  /* -------------------- OPTIONS -------------------- */
  const level1Options = useMemo(
    () => optionsData.map((o) => ({ label: o.name, value: o })),
    [optionsData]
  );

  /* -------------------- MAP EDIT DATA → UI STATE -------------------- */
  useEffect(() => {
    if (!editData || optionsData.length === 0) return;

    const mapped = editData.adjustments.map((adj) => {
      const lvl1 = optionsData.find((o) => o.name === adj.levelOne);

      const lvl2 = lvl1?.subgroup?.find((s) => s.name === adj.levelTwo);

      const lvl3 = lvl2?.subgroup?.find((s) => s.name === adj.levelThree);

      return {
        levelone: lvl1 || null,
        levelTwo: lvl2 || null,
        levelThree: lvl3 || null,
        currentAmount: adj.currentYear,
        previousAmount: adj.previousYear,
      };
    });

    setParticulars(mapped);
  }, [editData, optionsData]);

  /* -------------------- ROW HANDLERS -------------------- */
  const handleChange = (index, field, value) => {
    const updated = [...particulars];
    updated[index][field] = value;
    setParticulars(updated);
  };

  const handleAddParticular = () => {
    setParticulars([
      ...particulars,
      {
        levelone: null,
        levelTwo: null,
        levelThree: null,
        currentAmount: "",
        previousAmount: "",
      },
    ]);
  };

  const handleRemoveParticular = (index) => {
    setParticulars(particulars.filter((_, i) => i !== index));
  };

  /* -------------------- SUBMIT -------------------- */
  const handleSubmit = () => {
    const payload = {
      adjustmentDate: date ? dayjs(date).format("DD-MM-YYYY") : null,
      remarks,
      adjustments: particulars.map((p) => ({
        levelOne: p.levelone?.name ?? "",
        levelTwo: p.levelTwo?.name ?? "",
        levelThree: p.levelThree?.name ?? "",
        currentYear: p.currentAmount,
        previousYear: p.previousAmount,
      })),
    };

    if (editData) {
      editConsolManualAdjustmentApiHandler(
        { consolID: consolID, manualAdjustmentID: editData?._id },
        payload
      )
        .then((response) => {
          if (response?.data?.success === true) {
            handleClose();
            getManualAdjustments();
            swal("Adjustment updated successful", "", "success");
          } else {
            swal(
              "Error",
              response?.data?.error || "Unable to update Adjustment",
              "error"
            );
          }
        })
        .catch((error) => {
          getManualAdjustments();
          swal(
            "Error",
            error?.response?.data?.error || "Unable to update Adjustment",
            "error"
          );
        });
    } else {
      addConsolManualAdjustmentApiHandler({ consolID: consolID }, payload)
        .then((response) => {
          if (response?.data?.success === true) {
            handleClose();
            getManualAdjustments();
            swal("Adjustment added successful", "", "success");
          } else {
            swal(
              "Error",
              response?.data?.error || "Unable to add Adjustment",
              "error"
            );
          }
        })
        .catch((error) => {
          getManualAdjustments();
          swal(
            "Error",
            error?.response?.data?.error || "Unable to add Adjustment",
            "error"
          );
        });
    }
  };

  /* -------------------- UI -------------------- */
  return (
    <Modal
      width="650px"
      title={editData ? "Edit Entry" : "Create Entry"}
      bodycss={commoncss.unsetPadding}
      headerActions={<CloseIcon className="clickable" onClick={handleClose} />}
      footerActions={
        <>
          <Button
            btnColor={buttoncss.secondary}
            label="Cancel"
            onClick={handleClose}
          />
          <Button
            btnColor={buttoncss.primary}
            label={editData ? "Update Entry" : "Create Entry"}
            onClick={handleSubmit}
          />
        </>
      }
    >
      <div className={commoncss.pd1530}>
        {/* DATE */}
        <InputGroup
          label="Choose Date"
          class2_="mt-1"
          inputs={[
            <>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileDatePicker
                  className={`${commoncss.datePickerContainer} ${commoncss.clickable}`}
                  format="DD-MMM-YYYY"
                  slotProps={{
                    textField: {
                      InputProps: {
                        disableUnderline: true,
                      },
                      inputProps: {
                        placeholder: "DD-MMM-YYYY",
                      },
                      sx: {
                        "& input::placeholder": {
                          color: "#B3C0BD",
                          fontWeight: 400,
                          opacity: 1,
                        },
                        "& fieldset": {
                          border: "none",
                        },
                      },
                    },
                  }}
                  value={date}
                  onChange={setDate}
                />
              </LocalizationProvider>
              <span className="icon" style={{ marginTop: "-4px" }}>
                <CalenderIcon width={20} height={20} />
              </span>
            </>,
          ]}
        />

        {/* REMARKS */}
        <InputGroup
          label="Remarks"
          inputs={[
            <textarea
              rows={3}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter remarks"
              className={commoncss.placeholderStyle}
            />,
          ]}
        />

        {/* ADJUSTMENTS */}
        {particulars.map((item, index) => (
          <div
            key={index}
            style={{
              border: "1px solid #EAEEED",
              padding: "10px",
              borderRadius: "10px",
              marginTop: "15px",
            }}
          >
            {/* LEVEL 1 */}
            <InputGroup
              label={
                <div className="attachments-button-label">
                  <span>Level 1</span>
                  {particulars.length > 1 && (
                    <span
                    className="mb-2"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleRemoveParticular(index)}
                    >
                      <DeleteGreyIcon />
                    </span>
                  )}
                </div>
              }
              inputs={[
                <ConsolDropdown
                  options={level1Options}
                  selected={
                    item.levelone
                      ? { label: item.levelone.name, value: item.levelone }
                      : null
                  }
                  onSelect={(sel) => handleChange(index, "levelone", sel.value)}
                  placeHolder="Select Level 1"
                  className={commoncss.placeholderStyle}
                />,
              ]}
            />

            {/* LEVEL 2 */}
            <InputGroup
              label="Level 2"
              inputs={[
                <ConsolDropdown
                  options={
                    item.levelone?.subgroup?.map((s) => ({
                      label: s.name,
                      value: s,
                    })) || []
                  }
                  selected={
                    item.levelTwo
                      ? { label: item.levelTwo.name, value: item.levelTwo }
                      : null
                  }
                  onSelect={(sel) => handleChange(index, "levelTwo", sel.value)}
                  disabled={!item.levelone}
                  placeHolder="Select Level 2"
                  className={commoncss.placeholderStyle}
                />,
              ]}
            />

            {/* LEVEL 3 */}
            <InputGroup
              label="Level 3"
              inputs={[
                <ConsolDropdown
                  options={
                    item.levelTwo?.subgroup
                      ?.filter((s) => s.lineType === "line item")
                      .map((s) => ({
                        label: s.name,
                        value: s,
                      })) || []
                  }
                  selected={
                    item.levelThree
                      ? {
                          label: item.levelThree.name,
                          value: item.levelThree,
                        }
                      : null
                  }
                  onSelect={(sel) =>
                    handleChange(index, "levelThree", sel.value)
                  }
                  disabled={!item.levelTwo}
                  placeHolder="Select Level 3"
                  className={commoncss.placeholderStyle}
                />,
              ]}
            />

            <div className="row">
              <div className="col-md-6" style={{ paddingRight: "7px" }} >
                <InputGroup
                  label="Current Year Amount"
                  inputs={[
                    <input
                      type="text"
                      placeholder="Enter Amount"
                      className={commoncss.placeholderStyle}
                      value={item.currentAmount}
                      onChange={(e) =>
                        handleChange(index, "currentAmount", e.target.value)
                      }
                    />,
                  ]}
                />
              </div>
              <div className="col-md-6" style={{ paddingLeft: "7px" }}>
                <InputGroup
                  label="Previous Year Amount"
                  inputs={[
                    <input
                      type="text"
                      placeholder="Enter Amount"
                      value={item.previousAmount}
                      className={commoncss.placeholderStyle}
                      onChange={(e) =>
                        handleChange(index, "previousAmount", e.target.value)
                      }
                    />,
                  ]}
                />
              </div>
            </div>
          </div>
        ))}

        <div
          onClick={handleAddParticular}
          style={{
            color: "#2D8861",
            fontSize: "14px",
            marginTop: "10px",
            cursor: "pointer",
          }}
        >
          <PlusPrimaryIcon /> Add Field
        </div>
      </div>
    </Modal>
  );
};

export default CreateEditEntryModal;
